'use client';

import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { useActiveAccountId } from '@frameio/shared/contexts';
import {
  CreateProjectModal,
  type CreateProjectModalProps,
} from '../components/CreateProjectModal';

export type ShowCreateProjectsModalInput = {
  onRequestClose?: () => void;
  workspaceId: string;
  disableWorkspacePicker?: boolean;
};

export type UseCreateProjectModalInput = {
  onRequestClose?: () => void;
  workspaceId: string;
  projectId?: string;
  disableWorkspacePicker?: boolean;
};

export type UseCreateProjectModalReturn = {
  createProjectModal: React.ReactNode;
  showCreateProjectModal: (input: ShowCreateProjectsModalInput) => void;
  hideCreateProjectModal: () => void;
};

export const useCreateProjectModal = (): UseCreateProjectModalReturn => {
  const accountId = useActiveAccountId();
  const [props, setProps] = useState<
    Omit<CreateProjectModalProps, 'accountId'>
  >({
    onRequestClose: noop,
    isShowing: false,
    workspaceId: '',
    disableWorkspacePicker: true,
  });

  const createProjectModal = useMemo(
    () => <CreateProjectModal {...props} accountId={accountId ?? ''} />,
    [props, accountId]
  );

  const hideCreateProjectModal = useCallback(() => {
    setProps((prev) => ({
      ...prev,
      isShowing: false,
      onRequestClose: noop,
      workspaceId: '',
      projectId: '',
    }));
  }, []);

  const showCreateProjectModal = useCallback(
    ({
      onRequestClose = hideCreateProjectModal,
      workspaceId,
      projectId,
      disableWorkspacePicker = true,
    }: UseCreateProjectModalInput) => {
      setProps((prev) => ({
        ...prev,
        isShowing: true,
        onRequestClose,
        workspaceId,
        projectId,
        disableWorkspacePicker,
      }));
    },
    [hideCreateProjectModal]
  );

  return {
    createProjectModal,
    showCreateProjectModal,
    hideCreateProjectModal,
  };
};
